import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { 
  Megaphone, 
  Plus, 
  Pin,
  Calendar,
  Eye,
  Trash2,
  Edit,
  AlertCircle,
  CheckCircle,
  Clock,
  Bell,
  Users,
  X,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "general", label: "General", color: "bg-slate-100 text-slate-700" },
  { value: "academic", label: "Academic", color: "bg-blue-100 text-blue-700" },
  { value: "event", label: "Event", color: "bg-purple-100 text-purple-700" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
  { value: "billing", label: "Billing", color: "bg-amber-100 text-amber-700" }
];

const PRIORITIES = [
  { value: "low", label: "Low", icon: Clock },
  { value: "normal", label: "Normal", icon: Bell },
  { value: "high", label: "High", icon: AlertCircle },
  { value: "urgent", label: "Urgent", icon: AlertCircle }
];

export default function SchoolAnnouncements() {
  const { admin, school } = useOutletContext();
  const [announcements, setAnnouncements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "general",
    priority: "normal",
    target_grades: [],
    target_sections: [],
    is_pinned: false,
    expiry_date: ""
  });

  const grades = school?.grades_offered || [5, 6, 7, 8];
  const sections = ["A", "B", "C", "D"];

  useEffect(() => {
    fetchAnnouncements();
    fetchStats();
  }, [categoryFilter]);

  const fetchAnnouncements = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append("category", categoryFilter);
      params.append("include_expired", "true");
      
      const response = await fetch(`${API}/school/announcements/list?${params}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/school/announcements/stats`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error("Title and content are required");
      return;
    }
    
    try {
      const response = await fetch(`${API}/school/announcements/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          expiry_date: formData.expiry_date || null
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Show email notification info for urgent/high priority
        if (data.email_notifications) {
          toast.success("Announcement created! " + data.email_notifications);
        } else {
          toast.success("Announcement created");
        }
        setShowCreateModal(false);
        resetForm();
        fetchAnnouncements();
        fetchStats();
      } else {
        toast.error(data.error || "Failed to create announcement");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/school/announcements/${selectedAnnouncement.announcement_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Announcement updated");
        setShowEditModal(false);
        fetchAnnouncements();
      } else {
        toast.error(data.error || "Failed to update");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleDelete = async (announcementId) => {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    
    try {
      const response = await fetch(`${API}/school/announcements/${announcementId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Announcement deleted");
        fetchAnnouncements();
        fetchStats();
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleEdit = (announcement) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      category: announcement.category,
      priority: announcement.priority,
      target_grades: announcement.target_grades || [],
      target_sections: announcement.target_sections || [],
      is_pinned: announcement.is_pinned,
      expiry_date: announcement.expiry_date?.split("T")[0] || ""
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      category: "general",
      priority: "normal",
      target_grades: [],
      target_sections: [],
      is_pinned: false,
      expiry_date: ""
    });
  };

  const toggleGrade = (grade) => {
    setFormData(prev => ({
      ...prev,
      target_grades: prev.target_grades.includes(grade)
        ? prev.target_grades.filter(g => g !== grade)
        : [...prev.target_grades, grade]
    }));
  };

  const toggleSection = (section) => {
    setFormData(prev => ({
      ...prev,
      target_sections: prev.target_sections.includes(section)
        ? prev.target_sections.filter(s => s !== section)
        : [...prev.target_sections, section]
    }));
  };

  const getCategoryBadge = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? <Badge className={cat.color}>{cat.label}</Badge> : <Badge>{category}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Announcements</h1>
          <p className="text-slate-500 text-sm">Communicate with parents and students</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-announcement-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.active}</p>
                  <p className="text-xs text-slate-500">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.recent_views}</p>
                  <p className="text-xs text-slate-500">Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.by_category?.urgent || 0}</p>
                  <p className="text-xs text-slate-500">Urgent</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={categoryFilter || "all"} onValueChange={(val) => setCategoryFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Announcements List */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {announcements.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-1">No announcements</h3>
              <p className="text-slate-500 text-sm">Create your first announcement to communicate with parents</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {announcements.map((announcement) => (
                <div 
                  key={announcement.announcement_id} 
                  className="p-4 hover:bg-slate-50"
                  data-testid={`announcement-${announcement.announcement_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {announcement.is_pinned && (
                          <Pin className="w-4 h-4 text-amber-500" />
                        )}
                        <h3 className="font-semibold text-slate-800">{announcement.title}</h3>
                        {getCategoryBadge(announcement.category)}
                        {announcement.priority === "urgent" && (
                          <Badge className="bg-red-500 text-white">Urgent</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{announcement.content}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(announcement.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {announcement.views_count} views
                        </span>
                        {announcement.target_grades?.length > 0 && (
                          <span>
                            Grades: {announcement.target_grades.join(", ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(announcement)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDelete(announcement.announcement_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? "Edit Announcement" : "Create Announcement"}
            </DialogTitle>
            <DialogDescription>
              {showEditModal ? "Update the announcement details" : "Create a new announcement for parents and students"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Announcement title"
                data-testid="announcement-title"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Content *</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your announcement..."
                rows={5}
                data-testid="announcement-content"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Category</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Priority</label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(formData.priority === "urgent" || formData.priority === "high") && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    Email notifications will be sent to parents (Pro plan)
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Target Grades (leave empty for all)</label>
              <div className="flex flex-wrap gap-2">
                {grades.map(grade => (
                  <Button
                    key={grade}
                    type="button"
                    variant={formData.target_grades.includes(grade) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleGrade(grade)}
                    className={formData.target_grades.includes(grade) ? "bg-emerald-600" : ""}
                  >
                    Grade {grade}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Target Sections (leave empty for all)</label>
              <div className="flex flex-wrap gap-2">
                {sections.map(section => (
                  <Button
                    key={section}
                    type="button"
                    variant={formData.target_sections.includes(section) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleSection(section)}
                    className={formData.target_sections.includes(section) ? "bg-emerald-600" : ""}
                  >
                    Section {section}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Expiry Date (optional)</label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                />
              </div>
              
              <div className="flex items-center gap-2 pt-6">
                <Checkbox
                  id="is_pinned"
                  checked={formData.is_pinned}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_pinned: checked }))}
                />
                <label htmlFor="is_pinned" className="text-sm text-slate-700">Pin this announcement</label>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                {showEditModal ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
